import { parseISO } from 'date-fns';
import { useMemo } from 'react';

import { applyFilterDefaults } from '../../../../shared/issuesV2/snapshots';
import { useParams, useQuery } from '../../../contexts/RouterContext';
import type { SnapshotFilters } from '../../../../shared/issuesV2/snapshots';

const parseUTCDateToLocal = (dateString: string): Date => {
  // If it's already a full ISO string, just parse it directly
  if (dateString.includes('T')) {
    return parseISO(dateString);
  }
  // If it's just a date string, parse as UTC
  return parseISO(`${dateString}T00:00:00.000Z`);
};

const parserFn = (store: Record<string, any>): SnapshotFilters => {
  let teamId: SnapshotFilters['teamId'];
  // teamId is an array of numbers in this case
  if (store.teamId && store.teamId !== 'null') {
    teamId = (store.teamId ?? []).map((i: unknown) => Number(i));
  } else {
    teamId = store.teamId;
  }

  return applyFilterDefaults({
    labels: (store.labels ?? []).map((i: unknown) => Number(i)),
    start: store.start ? parseUTCDateToLocal(store.start) : undefined,
    end: store.end ? parseUTCDateToLocal(store.end) : undefined,
    category: store.category,
    teamId,
  });
};

/**
 * Hook for standardizing access to `useQuery` for the Issue Overview page. Lets us avoid
 * duplicating the `parserFn` passed in to `useQuery`.
 */
const useIssueOverview = () => {
  const [filters, setFilters] = useQuery<SnapshotFilters>(parserFn);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = useParams<{ project_id?: string }>();

  const isProjectPage = !!params.project_id;

  // Not memoizing this causes infinite rerendering.
  const filledFilters = useMemo(() => {
    return {
      ...filters,
      // If we have a params.project_id, that means we're viewing this from a Project page and not the global page.
      projectId: params.project_id,
      // We don't want to provide teamId on the project page.
      teamId: !isProjectPage ? filters.teamId : undefined,
    };
  }, [filters, params.project_id, isProjectPage]);

  return {
    filters: filledFilters,
    setFilters,
    isProjectPage,
  };
};

export default useIssueOverview;
